import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithCredential
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { auth, db, handleFirebaseError } from '../firebase.config';
import { Preferences } from '@capacitor/preferences';
import { Device } from '@capacitor/device';
import { BiometricAuth, BiometryType } from '@aparajita/capacitor-biometric-auth';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

// 🔐 Enhanced Capacitor Authentication Service
export class CapacitorAuthService {
  static currentUser = null;
  static authStateListeners = new Set();
  static deviceInfo = null;
  static isBiometricAvailable = false;
  static biometricType = null;

  // Initialize Capacitor-specific authentication
  static async init() {
    try {
      console.log('🚀 Initializing Capacitor Auth Service...');
      
      // Get device information
      this.deviceInfo = await this.getDeviceInfo();
      console.log('📱 Device Info:', this.deviceInfo);
      
      // Check biometric availability
      await this.checkBiometricAvailability();
      
      // Set up auth state listener
      this.setupAuthStateListener();
      
      // Initialize push notifications
      await this.initializePushNotifications();
      
      // Check for existing persistent session
      await this.checkPersistentSession();
      
      console.log('✅ Capacitor Auth Service initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize Capacitor Auth Service:', error);
      throw error;
    }
  }

  // Get comprehensive device information
  static async getDeviceInfo() {
    try {
      const deviceInfo = await Device.getInfo();
      const deviceId = await Device.getId();
      
      return {
        deviceId: deviceId.identifier,
        model: deviceInfo.model,
        platform: deviceInfo.platform,
        operatingSystem: deviceInfo.operatingSystem,
        osVersion: deviceInfo.osVersion,
        manufacturer: deviceInfo.manufacturer,
        isVirtual: deviceInfo.isVirtual,
        memUsed: deviceInfo.memUsed,
        diskFree: deviceInfo.diskFree,
        diskTotal: deviceInfo.diskTotal,
        webViewVersion: deviceInfo.webViewVersion,
        appVersion: deviceInfo.appVersion,
        appBuild: deviceInfo.appBuild,
        appId: deviceInfo.appId,
        appName: deviceInfo.appName,
        bundleId: deviceInfo.bundleId,
        isCapacitor: Capacitor.isNativePlatform(),
        isPluginAvailable: Capacitor.isPluginAvailable('BiometricAuth')
      };
    } catch (error) {
      console.error('Error getting device info:', error);
      return {
        deviceId: 'unknown',
        platform: 'unknown',
        isCapacitor: Capacitor.isNativePlatform()
      };
    }
  }

  // Check if biometric authentication is available
  static async checkBiometricAvailability() {
    try {
      if (!Capacitor.isPluginAvailable('BiometricAuth')) {
        console.log('📱 BiometricAuth plugin not available');
        this.isBiometricAvailable = false;
        return;
      }

      const result = await BiometricAuth.checkBiometry();
      this.isBiometricAvailable = result.isAvailable;
      this.biometricType = result.biometryType;

      console.log('🔐 Biometric availability:', {
        available: this.isBiometricAvailable,
        type: this.biometricType
      });
    } catch (error) {
      console.error('❌ Failed to check biometric availability:', error);
      this.isBiometricAvailable = false;
    }
  }

  // Set up Firebase auth state listener
  static setupAuthStateListener() {
    onAuthStateChanged(auth, async (user) => {
      console.log('🔥 Auth state changed:', user ? 'User logged in' : 'User logged out');
      
      this.currentUser = user;
      
      if (user) {
        // User is logged in
        await this.handleUserLogin(user);
      } else {
        // User is logged out
        await this.handleUserLogout();
      }
      
      // Notify all listeners
      this.notifyAuthStateListeners(user);
    });
  }

  // Handle user login
  static async handleUserLogin(user) {
    try {
      console.log('👤 Handling user login:', user.uid);
      
      // Save user data to preferences
      await this.saveUserToPreferences(user);
      
      // Register device if not already registered
      await this.registerDeviceIfNeeded(user);
      
      // Set up push notifications for this user
      await this.setupUserPushNotifications(user);
      
      console.log('✅ User login handled successfully');
    } catch (error) {
      console.error('❌ Error handling user login:', error);
    }
  }

  // Handle user logout
  static async handleUserLogout() {
    try {
      console.log('👋 Handling user logout');
      
      // Clear user data from preferences
      await this.clearUserFromPreferences();
      
      // Clear push notification tokens
      await this.clearPushNotificationTokens();
      
      console.log('✅ User logout handled successfully');
    } catch (error) {
      console.error('❌ Error handling user logout:', error);
    }
  }

  // Save user data to Capacitor preferences
  static async saveUserToPreferences(user) {
    try {
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        lastLogin: new Date().toISOString(),
        deviceId: this.deviceInfo?.deviceId
      };

      await Preferences.set({
        key: 'acctoo_user_data',
        value: JSON.stringify(userData)
      });

      // Also save auth token for persistence
      const token = await user.getIdToken();
      await Preferences.set({
        key: 'acctoo_auth_token',
        value: token
      });

      console.log('💾 User data saved to preferences');
    } catch (error) {
      console.error('❌ Error saving user to preferences:', error);
    }
  }

  // Clear user data from preferences
  static async clearUserFromPreferences() {
    try {
      await Preferences.remove({ key: 'acctoo_user_data' });
      await Preferences.remove({ key: 'acctoo_auth_token' });
      await Preferences.remove({ key: 'acctoo_biometric_enabled' });
      
      console.log('🗑️ User data cleared from preferences');
    } catch (error) {
      console.error('❌ Error clearing user from preferences:', error);
    }
  }

  // Check for existing persistent session
  static async checkPersistentSession() {
    try {
      const userData = await Preferences.get({ key: 'acctoo_user_data' });
      const authToken = await Preferences.get({ key: 'acctoo_auth_token' });
      
      if (userData.value && authToken.value) {
        console.log('🔍 Found persistent session, validating...');
        
        // Validate token with Firebase
        try {
          // Firebase will automatically restore the session
          // The auth state listener will handle the rest
          console.log('✅ Persistent session validated');
        } catch (error) {
          console.log('❌ Persistent session invalid, clearing...');
          await this.clearUserFromPreferences();
        }
      }
    } catch (error) {
      console.error('❌ Error checking persistent session:', error);
    }
  }

  // Register device if needed
  static async registerDeviceIfNeeded(user) {
    try {
      if (!this.deviceInfo?.deviceId) {
        console.log('⚠️ No device ID available, skipping device registration');
        return;
      }

      // Check if device is already registered
      const deviceRef = doc(db, 'devices', this.deviceInfo.deviceId);
      const deviceSnap = await getDoc(deviceRef);
      
      if (!deviceSnap.exists()) {
        console.log('📱 Registering new device...');
        
        const deviceData = {
          deviceId: this.deviceInfo.deviceId,
          userId: user.uid,
          deviceInfo: this.deviceInfo,
          status: 'pending_approval',
          createdAt: serverTimestamp(),
          lastSeen: serverTimestamp(),
          platform: this.deviceInfo.platform,
          appVersion: this.deviceInfo.appVersion
        };
        
        await setDoc(deviceRef, deviceData);
        console.log('✅ Device registered successfully');
      } else {
        console.log('📱 Device already registered, updating last seen...');
        await updateDoc(deviceRef, {
          lastSeen: serverTimestamp(),
          userId: user.uid
        });
      }
    } catch (error) {
      console.error('❌ Error registering device:', error);
    }
  }

  // Initialize push notifications
  static async initializePushNotifications() {
    try {
      if (!Capacitor.isPluginAvailable('PushNotifications')) {
        console.log('📱 PushNotifications plugin not available');
        return;
      }

      // Request permission
      const permStatus = await PushNotifications.requestPermissions();
      if (permStatus.receive === 'granted') {
        console.log('✅ Push notification permission granted');
        
        // Register for push notifications
        await PushNotifications.register();
        
        // Listen for registration
        PushNotifications.addListener('registration', (token) => {
          console.log('📱 Push registration token:', token.value);
          this.savePushToken(token.value);
        });
        
        // Listen for push notifications
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('📱 Push notification received:', notification);
        });
        
        // Listen for push notification actions
        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.log('📱 Push notification action performed:', notification);
        });
      } else {
        console.log('❌ Push notification permission denied');
      }
    } catch (error) {
      console.error('❌ Error initializing push notifications:', error);
    }
  }

  // Set up user-specific push notifications
  static async setupUserPushNotifications(user) {
    try {
      if (!user) return;
      
      // Save push token to user's document
      const pushToken = await Preferences.get({ key: 'acctoo_push_token' });
      if (pushToken.value) {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          pushToken: pushToken.value,
          lastSeen: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('❌ Error setting up user push notifications:', error);
    }
  }

  // Save push token
  static async savePushToken(token) {
    try {
      await Preferences.set({
        key: 'acctoo_push_token',
        value: token
      });
      console.log('💾 Push token saved');
    } catch (error) {
      console.error('❌ Error saving push token:', error);
    }
  }

  // Clear push notification tokens
  static async clearPushNotificationTokens() {
    try {
      await Preferences.remove({ key: 'acctoo_push_token' });
      console.log('🗑️ Push tokens cleared');
    } catch (error) {
      console.error('❌ Error clearing push tokens:', error);
    }
  }

  // Login with email and password
  static async loginWithEmail(email, password) {
    try {
      console.log('🔐 Attempting email login...');
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      console.log('✅ Email login successful:', user.uid);
      return { success: true, user };
    } catch (error) {
      console.error('❌ Email login failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Login with Google
  static async loginWithGoogle() {
    try {
      console.log('🔐 Attempting Google login...');
      
      // For Capacitor, we need to use the Google Sign-In plugin
      // This is a placeholder - you'll need to implement the actual Google Sign-In
      throw new Error('Google Sign-In not implemented yet');
    } catch (error) {
      console.error('❌ Google login failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Login with biometric authentication
  static async loginWithBiometric() {
    try {
      if (!this.isBiometricAvailable) {
        throw new Error('Biometric authentication not available');
      }

      console.log('🔐 Attempting biometric login...');
      
      const result = await BiometricAuth.authenticate({
        reason: 'Authenticate to access ACCTOO',
        title: 'ACCTOO Authentication',
        subtitle: 'Use your biometric to sign in',
        description: 'Place your finger on the sensor or look at the camera',
        negativeButtonText: 'Cancel',
        maxAttempts: 3
      });

      if (result.succeeded) {
        // Retrieve stored credentials and login
        const userData = await Preferences.get({ key: 'acctoo_user_data' });
        if (userData.value) {
          const user = JSON.parse(userData.value);
          console.log('✅ Biometric login successful');
          return { success: true, user };
        } else {
          throw new Error('No stored user data found');
        }
      } else {
        throw new Error('Biometric authentication failed');
      }
    } catch (error) {
      console.error('❌ Biometric login failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Enable biometric authentication
  static async enableBiometric() {
    try {
      if (!this.isBiometricAvailable) {
        throw new Error('Biometric authentication not available');
      }

      console.log('🔐 Enabling biometric authentication...');
      
      // Test biometric authentication
      const result = await BiometricAuth.authenticate({
        reason: 'Enable biometric authentication for ACCTOO',
        title: 'Enable Biometric Login',
        subtitle: 'Set up biometric authentication',
        description: 'This will allow you to quickly sign in using your biometric',
        negativeButtonText: 'Cancel'
      });

      if (result.succeeded) {
        await Preferences.set({
          key: 'acctoo_biometric_enabled',
          value: 'true'
        });
        
        console.log('✅ Biometric authentication enabled');
        return { success: true };
      } else {
        throw new Error('Biometric authentication failed');
      }
    } catch (error) {
      console.error('❌ Error enabling biometric:', error);
      return { success: false, error: error.message };
    }
  }

  // Disable biometric authentication
  static async disableBiometric() {
    try {
      await Preferences.remove({ key: 'acctoo_biometric_enabled' });
      console.log('✅ Biometric authentication disabled');
      return { success: true };
    } catch (error) {
      console.error('❌ Error disabling biometric:', error);
      return { success: false, error: error.message };
    }
  }

  // Check if biometric is enabled
  static async isBiometricEnabled() {
    try {
      const result = await Preferences.get({ key: 'acctoo_biometric_enabled' });
      return result.value === 'true';
    } catch (error) {
      console.error('❌ Error checking biometric status:', error);
      return false;
    }
  }

  // Logout user
  static async logout() {
    try {
      console.log('👋 Logging out user...');
      
      await signOut(auth);
      await this.clearUserFromPreferences();
      
      console.log('✅ User logged out successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Logout failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Add auth state listener
  static onAuthStateChanged(callback) {
    this.authStateListeners.add(callback);
    return () => this.authStateListeners.delete(callback);
  }

  // Notify all auth state listeners
  static notifyAuthStateListeners(user) {
    this.authStateListeners.forEach(callback => {
      try {
        callback(user);
      } catch (error) {
        console.error('Auth state listener error:', error);
      }
    });
  }

  // Get current user
  static getCurrentUser() {
    return this.currentUser;
  }

  // Check if user is authenticated
  static isAuthenticated() {
    return !!this.currentUser;
  }

  // Get device info
  static getDeviceInfo() {
    return this.deviceInfo;
  }

  // Check if biometric is available
  static isBiometricAvailable() {
    return this.isBiometricAvailable;
  }

  // Safe biometric availability check with error handling
  static async safeCheckBiometricAvailability() {
    try {
      await this.checkBiometricAvailability();
      return this.isBiometricAvailable;
    } catch (error) {
      console.error('❌ Failed to check biometric availability:', error);
      return false;
    }
  }

  // Get biometric type
  static getBiometricType() {
    return this.biometricType;
  }
}

export default CapacitorAuthService;

